import { HeadDataType } from 'components/head-data';
import { Breadcrumb } from 'components/breadcrumbs';
import {
  getTargetedUrlPropertyTypeSelection,
  HOMES_TARGETED_URL_PROPERTY_TYPE,
  HOUSES_TARGETED_URL_PROPERTY_TYPE,
  CONDO_TARGETED_URL_PROPERTY_TYPE,
  TOWNHOUSE_TARGETED_URL_PROPERTY_TYPE,
  FARMS_TARGETED_URL_PROPERTY_TYPE,
  COMMERCIAL_TARGETED_URL_PROPERTY_TYPE,
  LAND_TARGETED_URL_PROPERTY_TYPE,
} from 'utils/listing-query-helper';
import {
  LocationInfo,
  ListingFilters,
  SchemaQuestion,
  PropertyInsights,
  BreadcrumbList,
  ProductSchema,
} from './types';
import { formatI18nNumber } from 'utils/i18n-format-currency';
import {
  createBreadcrumbList,
  createProductSchema,
  createFAQPageSchema,
} from './types/schema';
import { SchemaBuilder } from './types/schema-builder';
import { AVAILABLE_STATUS, NOT_AVAILABLE_OTHER_STATUS, NOT_AVAILABLE_SOLD_STATUS } from 'contexts/preferences/listing-params/types';
import { HrefLangLink } from './build-head-tags';
import { LOCALE_COMMON_PATHS } from 'types/locales';
import type { TFunction } from 'i18next';

/**
 * Translates a property type label using the translation function.
 * Maps property type constants to their translation keys.
 * 
 * @param propertyType - The property type label (e.g., 'Houses', 'Condos')
 * @param t - The translation function
 * @param useRealEstateAndHomes - Whether to use "Real Estate & Homes" for the default case
 * @returns The translated property type string
 */
function translatePropertyType(propertyType: string, t: TFunction, useRealEstateAndHomes = false): string {
  switch (propertyType) {
    case HOUSES_TARGETED_URL_PROPERTY_TYPE:
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_HOUSES);
    case CONDO_TARGETED_URL_PROPERTY_TYPE:
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_CONDOS);
    case TOWNHOUSE_TARGETED_URL_PROPERTY_TYPE:
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_TOWNHOUSES);
    case FARMS_TARGETED_URL_PROPERTY_TYPE:
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_FARMS);
    case COMMERCIAL_TARGETED_URL_PROPERTY_TYPE:
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_COMMERCIAL);
    case LAND_TARGETED_URL_PROPERTY_TYPE:
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_LAND);
    case HOMES_TARGETED_URL_PROPERTY_TYPE:
    default:
      return useRealEstateAndHomes 
        ? t(LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_REAL_ESTATE_AND_HOMES)
        : t(LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_HOMES);
  }
}

/**
 * Builder class for constructing head tags data.
 *
 * @example
 * ```typescript
 * const builder = new HeadTagsBuilder();
 * builder.setCanonicalUrl('https://www.zoocasa.com/toronto-on-real-estate');
 * builder.setTitle('Zoocasa', 'Toronto', { homeType: 'Houses', rental: false, status: 'Available' });
 * const headTags = builder.build();
 *
 * // headTags is now:
 * {
 *   canonicalUrl: "https://www.zoocasa.com/toronto-on-real-estate",
 *   title: "Toronto Real Estate | Zoocasa",
 * }
 */

interface TitleParams {
  siteName: string;
  areaName: string;
  filters: ListingFilters;
  city?: string;
  neighbourhood?: string;
}

export class HeadTagsBuilder {
  private hrefLangs: HrefLangLink[];
  private canonicalUrl: string;
  private schema: Record<string, unknown>[] = [];
  private title: string;
  private metaDescription: string;
  private breadcrumbList?: BreadcrumbList;
  private productSchema?: ProductSchema;
  private numberOfHomesQuestion?: SchemaQuestion;
  private averagePriceQuestion?: SchemaQuestion;
  private propertyTypeQuestion?: SchemaQuestion;
  private locale?: string;

  /**
   * Builds and returns the complete head data object containing SEO metadata and schema markup.
   *
   * @returns An object containing:
   *   - canonicalUrl: The canonical URL for the page
   *   - title: The page title
   *   - metaDescription: The meta description
   *   - schema: An array of schema.org structured data objects
   *
   * @example
   * ```typescript
   * // Example return value:
   * {
   *   canonicalUrl: "https://www.zoocasa.com/toronto-on-real-estate",
   *   title: "Toronto Real Estate | Zoocasa",
   *   metaDescription: "Browse 1,234 homes for sale in Toronto. View listing photos, property details, and prices.",
   *   schema: [
   *     // BreadcrumbList schema
   *     {
   *       "@type": "BreadcrumbList",
   *       "itemListElement": [...]
   *     },
   *     // Product schema
   *     {
   *       "@type": "Product",
   *       "name": "Toronto Real Estate",
   *       "offers": {...}
   *     },
   *     // FAQ Page schema
   *     {
   *       "@type": "FAQPage",
   *       "mainEntity": [
   *         {
   *           "@type": "Question",
   *           "name": "How many homes are there for sale in Toronto?",
   *           "acceptedAnswer": {...}
   *         }
   *         // ... additional questions
   *       ]
   *     }
   *   ]
   * }
   * ```
   */
  build(): HeadDataType {
    this.buildSchema();

    return {
      canonicalUrl: this.canonicalUrl,
      title: this.title,
      metaDescription: this.metaDescription,
      schema: this.schema,
      hrefLangs: this.hrefLangs,
    };
  }

  /**
   * Sets the hrefLang's for the page.
   *
   * @param languages - The hrefLang languages to set
   * @returns The builder instance for method chaining
   *
   * @example
   * Affects the following in the output:
   * ```typescript
   * {
   *   hrefLangs: ["en-ca"]
   *   // ... other properties
   * }
   * ```
   */
  setHrefLangs(languages: HrefLangLink[]): HeadTagsBuilder {
    this.hrefLangs = languages;
    return this;
  }

  /**
   * Sets the locale for schema inLanguage properties.
   *
   * @param locale - The locale code (e.g., 'en-CA', 'fr-CA')
   * @returns The builder instance for method chaining
   */
  setLocale(locale?: string): HeadTagsBuilder {
    this.locale = locale;
    return this;
  }

  /**
   * Sets the canonical URL for the page.
   *
   * @param url - The canonical URL to set
   * @returns The builder instance for method chaining
   *
   * @example
   * Affects the following in the output:
   * ```typescript
   * {
   *   canonicalUrl: "https://www.zoocasa.com/toronto-on-real-estate"
   *   // ... other properties
   * }
   * ```
   */
  setCanonicalUrl(url: string): HeadTagsBuilder {
    this.canonicalUrl = url;
    return this;
  }

  /**
   * Sets the page title using the site name, area name, and filters.
   *
   * @param params - Title parameters including siteName, areaName, filters, city, neighbourhood
   * @param t - Translation function for server-side translation
   * @returns The builder instance for method chaining
   *
   * @example
   * Affects the following in the output:
   * ```typescript
   * {
   *   title: "Toronto Real Estate | Zoocasa"
   *   // ... other properties
   * }
   * ```
   */
  setTitle(params: TitleParams, t: TFunction): HeadTagsBuilder {
    this.title = this.createTitle(params, t);
    return this;
  }

  /**
   * Sets the meta description using area name, filters, and listing count.
   *
   * @param areaName - The name of the geographic area
   * @param filters - The applied listing filters
   * @param listingsCount - The total number of listings
   * @param t - Translation function for server-side translation
   * @returns The builder instance for method chaining
   *
   * @example
   * Affects the following in the output:
   * ```typescript
   * {
   *   metaDescription: "Browse 1,234 homes for sale in Toronto. View listing photos, property details, and prices."
   *   // ... other properties
   * }
   * ```
   */
  setMetaDescription(areaName: string, filters: ListingFilters, listingsCount: number | undefined, t: TFunction): HeadTagsBuilder {
    this.metaDescription = this.createMetaDescription(areaName, filters, listingsCount, t);
    return this;
  }

  /**
   * Sets the breadcrumb list schema markup.
   *
   * @param breadcrumbs - Array of breadcrumb navigation items
   * @returns The builder instance for method chaining
   *
   * @example
   * Affects the following in the output:
   * ```typescript
   * {
   *   schema: [{
   *     "@type": "BreadcrumbList",
   *     "itemListElement": [
   *       {
   *         "@type": "ListItem",
   *         "position": 1,
   *         "name": "ON",
   *         "item": "https://www.zoocasa.com/ontario-real-estate"
   *       },
   *       // ... more breadcrumb items
   *     ]
   *   },
   *   // ... other schema objects
   *   ]
   * }
   * ```
   */
  setBreadcrumbList(breadcrumbs: readonly Breadcrumb[]): HeadTagsBuilder {
    const breadcrumbList = createBreadcrumbList(breadcrumbs);
    this.breadcrumbList = new SchemaBuilder(breadcrumbList).build();
    return this;
  }

  /**
   * Sets the product schema markup with pricing information.
   *
   * @param name - The product name (e.g., "Toronto Real Estate")
   * @param currency - The currency code (e.g., "CAD" or "USD")
   * @param highestPrice - The highest property price
   * @param lowestPrice - The lowest property price
   * @returns The builder instance for method chaining
   *
   * @example
   * Affects the following in the output:
   * ```typescript
   * {
   *   schema: [{
   *     "@type": "Product",
   *     "name": "Toronto Real Estate",
   *     "offers": {
   *       "@type": "AggregateOffer",
   *       "priceCurrency": "CAD",
   *       "lowPrice": 500000,
   *       "highPrice": 2000000
   *     }
   *   },
   *   // ... other schema objects
   *   ]
   * }
   * ```
   */
  setProduct(name: string, currency: string, highestPrice: number | null, lowestPrice: number | null): HeadTagsBuilder {
    const productSchema = createProductSchema(name, currency, highestPrice, lowestPrice);
    this.productSchema = new SchemaBuilder(productSchema).setInLanguage(this.locale).build();
    return this;
  }

  /**
   * Sets the FAQ schema question about the number of homes.
   *
   * @param homeType - The type of home (e.g., "Houses", "Condos")
   * @param isRental - Whether the listings are rentals
   * @param locationInfo - Location information object
   * @param listingsCount - The total number of listings
   * @returns The builder instance for method chaining
   *
   * @example
   * Affects the following in the output:
   * ```typescript
   * {
   *   schema: [{
   *     "@type": "FAQPage",
   *     "mainEntity": [{
   *       "@type": "Question",
   *       "name": "How many homes are there for sale in Toronto?",
   *       "acceptedAnswer": {
   *         "@type": "Answer",
   *         "text": "There are a total of 1,234 homes for sale in Toronto, Ontario."
   *       }
   *     },
   *     // ... other questions
   *     ]
   *   }]
   * }
   * ```
   */
  setNumberOfHomesQuestion(
    homeType: string,
    isRental: boolean,
    locationInfo: LocationInfo,
    listingsCount: number
  ): HeadTagsBuilder {
    this.numberOfHomesQuestion = this.createNumberOfHomesQuestion(
      homeType,
      isRental,
      locationInfo,
      listingsCount
    );
    return this;
  }

  /**
   * Sets the FAQ schema question about average property prices.
   *
   * @param insights - Property insights data containing average prices
   * @param isRental - Whether the listings are rentals
   * @param locationInfo - Location information object
   * @returns The builder instance for method chaining
   *
   * @example
   * Affects the following in the output:
   * ```typescript
   * {
   *   schema: [{
   *     "@type": "FAQPage",
   *     "mainEntity": [{
   *       "@type": "Question",
   *       "name": "What is the average listing price of a home for sale in Toronto?",
   *       "acceptedAnswer": {
   *         "@type": "Answer",
   *         "text": "The average listing price of all home types in Toronto, Ontario is $850,000."
   *       }
   *     },
   *     // ... other questions
   *     ]
   *   }]
   * }
   * ```
   */
  setAveragePriceQuestion(
    insights: PropertyInsights,
    isRental: boolean,
    locationInfo: LocationInfo
  ): HeadTagsBuilder {
    this.averagePriceQuestion = this.createAveragePriceQuestion(
      insights,
      isRental,
      locationInfo
    );
    return this;
  }

  /**
   * Sets the FAQ schema question about specific property type prices.
   *
   * @param propertyType - The type of property ("house", "townhouse", or "condo")
   * @param insights - Property insights data containing type-specific prices
   * @param locationInfo - Location information object
   * @param isRental - Whether the listings are rentals
   * @returns The builder instance for method chaining
   *
   * @example
   * Affects the following in the output:
   * ```typescript
   * {
   *   schema: [{
   *     "@type": "FAQPage",
   *     "mainEntity": [{
   *       "@type": "Question",
   *       "name": "How much does a condo cost in Toronto?",
   *       "acceptedAnswer": {
   *         "@type": "Answer",
   *         "text": "The average price of a condo for sale in Toronto, Ontario is $600,000."
   *       }
   *     },
   *     // ... other questions
   *     ]
   *   }]
   * }
   * ```
   */
  setPropertyTypeQuestion(
    propertyType: 'house' | 'townhouse' | 'condo',
    insights: PropertyInsights,
    locationInfo: LocationInfo,
    isRental: boolean
  ): HeadTagsBuilder {
    this.propertyTypeQuestion = this.createPropertyTypeQuestion(
      propertyType,
      insights,
      locationInfo,
      isRental
    );
    return this;
  }

  private createSchemaQuestion(question: string, answer: string): SchemaQuestion {
    return {
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    };
  }

  private createNumberOfHomesQuestion(
    homeType: string,
    isRental: boolean,
    locationInfo: LocationInfo,
    listingsCount: number
  ): SchemaQuestion {
    const { place, cityAndProvince } = locationInfo;
    const forSaleOrRent = isRental ? 'rent' : 'sale';
    return this.createSchemaQuestion(
      `How many ${homeType.toLowerCase()} are there for ${forSaleOrRent} in ${place}?`,
      `There are a total of ${formatI18nNumber(listingsCount)} ${homeType.toLowerCase()} for ${forSaleOrRent} in ${cityAndProvince}.`
    );
  }

  private createAveragePriceQuestion(
    insights: PropertyInsights,
    isRental: boolean,
    locationInfo: LocationInfo
  ): SchemaQuestion | null {
    const averagePrice = insights?.averagePrices?.total;
    if (!averagePrice) return null;
    const { place, cityAndProvince } = locationInfo;
    return this.createSchemaQuestion(
      `What is the average listing price of a home for ${isRental ? 'rent' : 'sale'} in ${place}?`,
      `The average listing price of all home types in ${cityAndProvince} is $${formatI18nNumber(averagePrice)}.`
    );
  }

  private createPropertyTypeQuestion(
    propertyType: 'house' | 'townhouse' | 'condo',
    insights: PropertyInsights,
    locationInfo: LocationInfo,
    isRental: boolean
  ): SchemaQuestion | null {
    const averagePrice = insights.averagePrices[propertyType];
    const count = insights.counts[propertyType];
    if (!averagePrice || !count) return null;
    const { place, cityAndProvince } = locationInfo;
    return this.createSchemaQuestion(
      `How much does a ${propertyType} cost in ${place}?`,
      `The average price of a ${propertyType} for ${isRental ? 'rent' : 'sale'} in ${cityAndProvince} is $${formatI18nNumber(averagePrice)}.`
    );
  }

  private createTitle(params: TitleParams, t: TFunction): string {
    const { areaName, filters, city, neighbourhood } = params;
    const { rental: isRental, status } = filters;
    const homeTypeLabel = getTargetedUrlPropertyTypeSelection(filters.homeType);
    const areaNameText = neighbourhood && city ? `${city}, ${areaName}` : areaName;
    // For titles, use "Real Estate & Homes" when no specific property type is selected
    const useRealEstateAndHomes = homeTypeLabel === HOMES_TARGETED_URL_PROPERTY_TYPE;
    const homeTypeText = translatePropertyType(homeTypeLabel, t, useRealEstateAndHomes);

    if (status === NOT_AVAILABLE_SOLD_STATUS && isRental) {
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_SEO_TITLE_RECENTLY_RENTED, { areaName: areaNameText, homeType: homeTypeText });
    } else if (status === NOT_AVAILABLE_SOLD_STATUS && !isRental) {
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_SEO_TITLE_RECENTLY_SOLD, { areaName: areaNameText, homeType: homeTypeText });
    } else if (status === AVAILABLE_STATUS && isRental) {
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_SEO_TITLE_FOR_RENT, { areaName: areaNameText, homeType: homeTypeText });
    } else if (status === AVAILABLE_STATUS && !isRental) {
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_SEO_TITLE_FOR_SALE, { areaName: areaNameText, homeType: homeTypeText });
    } else if (status === NOT_AVAILABLE_OTHER_STATUS) {
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_SEO_TITLE_PAST_LISTINGS, { areaName: areaNameText });
    }

    // Default case - for sale
    return t(LOCALE_COMMON_PATHS.AREA_PAGE_SEO_TITLE_FOR_SALE, { areaName: areaNameText, homeType: homeTypeText });
  }

  private createMetaDescription(areaName: string, filters: ListingFilters, listingsCount: number | undefined, t: TFunction): string {
    const { rental: isRental, status } = filters;
    const homeTypeLabel = getTargetedUrlPropertyTypeSelection(filters.homeType);
    // For meta descriptions, translate and use lowercase
    const homeTypeText = translatePropertyType(homeTypeLabel, t).toLowerCase();
    const totalListings = listingsCount > 0 ? `${formatI18nNumber(listingsCount)} ` : '';

    if (status === NOT_AVAILABLE_SOLD_STATUS) {
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_SEO_META_DESCRIPTION_SOLD, {
        totalListings,
        homeType: homeTypeText,
        areaName,
      });
    } else if (isRental) {
      return t(LOCALE_COMMON_PATHS.AREA_PAGE_SEO_META_DESCRIPTION_FOR_RENT, {
        totalListings,
        homeType: homeTypeText,
        areaName,
      });
    }

    return t(LOCALE_COMMON_PATHS.AREA_PAGE_SEO_META_DESCRIPTION_FOR_SALE, {
      totalListings,
      homeType: homeTypeText,
      areaName,
    });
  }

  private buildSchema(): void {
    this.schema = [];

    if (this.breadcrumbList) {
      this.schema.unshift(this.breadcrumbList);
    }

    if (this.productSchema) {
      this.schema.push(this.productSchema);
    }

    const questions: (SchemaQuestion | null)[] = [];

    if (this.numberOfHomesQuestion) {
      questions.push(this.numberOfHomesQuestion);
    }

    if (this.averagePriceQuestion) {
      questions.push(this.averagePriceQuestion);
    }

    if (this.propertyTypeQuestion) {
      questions.push(this.propertyTypeQuestion);
    }

    if (questions.length > 0) {
      const faqSchemaBase = createFAQPageSchema(questions);
      const faqSchema = new SchemaBuilder(faqSchemaBase).setInLanguage(this.locale).build();
      this.schema.push(faqSchema);
    }
  }
}
